import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useStore } from "../context/StoreContext";
import { formatINR, resolveMediaUrl } from "../lib/api";
import { FREE_SHIPPING_THRESHOLD } from "../lib/constants";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, subtotal, bundleDiscount } = useStore();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="bg-[#0A0A0A] border-l border-[#262626] text-[#F5F5F5] w-full sm:max-w-md p-0 grain"
      >
        <div className="flex flex-col h-full">
          <SheetHeader className="p-6 border-b border-[#262626]">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-serif-display text-2xl text-[#F5F5F5]">
                Your <span className="gold-text">Box</span>
              </SheetTitle>
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
              {cart.length} item{cart.length === 1 ? "" : "s"}
            </p>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-4">
                <div className="font-serif-display italic text-2xl text-[#A3A3A3]">
                  Nothing here yet.
                </div>
                <p className="text-sm text-[#737373]">Find something that speaks for you.</p>
                <button
                  className="btn-gold mt-4"
                  data-testid="cart-empty-shop-btn"
                  onClick={() => {
                    setCartOpen(false);
                    navigate("/shop");
                  }}
                >
                  Shop Now
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                {cart.map((item) => (
                  <div
                    key={item.product_id}
                    data-testid={`cart-item-${item.product_id}`}
                    className="flex gap-4 pb-5 border-b border-[#1a1a1a]"
                  >
                    <img
                      src={resolveMediaUrl(item.image)}
                      alt={item.name}
                      className="w-20 h-24 object-cover bg-[#141414]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-serif-display text-base truncate">{item.name}</h4>
                        <button
                          onClick={() => removeFromCart(item.product_id)}
                          data-testid={`cart-remove-${item.product_id}`}
                          className="text-[#737373] hover:text-[#D4AF37]"
                        >
                          <Trash2 strokeWidth={1.25} size={16} />
                        </button>
                      </div>
                      <div className="text-xs text-[#A3A3A3] mt-1">{formatINR(item.price)}</div>
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          data-testid={`cart-decrement-${item.product_id}`}
                          className="w-7 h-7 border border-[#262626] flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37]"
                        >
                          <Minus size={12} strokeWidth={1.5} />
                        </button>
                        <span className="text-sm w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          data-testid={`cart-increment-${item.product_id}`}
                          className="w-7 h-7 border border-[#262626] flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37]"
                        >
                          <Plus size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="border-t border-[#262626] p-6 space-y-4 bg-[#0A0A0A]">
              <FreeShippingBanner subtotal={subtotal} />
              {bundleDiscount?.total > 0 && (
                <div
                  data-testid="cart-bundle-savings"
                  className="border border-[#F5D565] bg-[#F5D565]/[0.06] px-3 py-2.5"
                >
                  <div className="font-ui" style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#F5D565", fontWeight: 700 }}>
                    ✦ Bundle Offer Applied
                  </div>
                  <div className="mt-1 font-body text-[13px]" style={{ color: "rgba(255,255,255,0.85)" }}>
                    You saved {formatINR(bundleDiscount.total)} · {bundleDiscount.applied[0]?.name}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A3A3A3] tracking-[0.2em] uppercase text-xs">Subtotal</span>
                <span className="font-serif-display text-2xl" data-testid="cart-subtotal">
                  {formatINR(subtotal - (bundleDiscount?.total || 0))}
                </span>
              </div>
              <p className="text-[10px] text-[#737373] tracking-[0.2em] uppercase">
                Taxes calculated at checkout
              </p>
              <p
                data-testid="cart-delivery-timeline"
                className="text-[10px] text-[#F5D565] tracking-[0.2em] uppercase"
              >
                ✦ Delivery in 5-7 working days
              </p>
              <button
                data-testid="cart-checkout-btn"
                className="btn-gold w-full"
                onClick={handleCheckout}
              >
                Proceed to Checkout
              </button>
              <p
                data-testid="cart-spam-folder-note"
                className="text-[10px] leading-relaxed text-[#A3A3A3] mt-1"
              >
                If order confirmation is not visible in your inbox, please check your spam folder.
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FreeShippingBanner({ subtotal }) {
  const reached = subtotal >= FREE_SHIPPING_THRESHOLD;
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const pct = Math.min(100, Math.round((subtotal / FREE_SHIPPING_THRESHOLD) * 100));
  return (
    <div data-testid="cart-free-shipping-banner" className="border px-3 py-2.5"
         style={{
           borderColor: reached ? "#3E7A4A" : "#F5D56540",
           background: reached ? "rgba(46,140,82,0.12)" : "rgba(245,213,101,0.05)",
         }}>
      <div className="text-[11px] tracking-[0.15em] uppercase" style={{ color: reached ? "#90D7A3" : "#F5D565" }}>
        {reached
          ? "🎉 Free delivery unlocked"
          : <>Add <span className="text-white">{formatINR(remaining)}</span> for FREE delivery</>
        }
      </div>
      <div className="mt-2 h-1 bg-[#262626] overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: reached ? "#3E7A4A" : "#F5D565" }} />
      </div>
    </div>
  );
}
